import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { 
  Search, Briefcase, Users, ChevronRight, MapPin, Calendar, 
  UserPlus, Filter, Eye
} from 'lucide-react';
import { toast } from 'sonner';

const API = '/api';

const PIPELINE_STAGES = [
  { key: 'new', label: 'New', color: 'bg-blue-500' },
  { key: 'screening', label: 'Screening', color: 'bg-yellow-500' },
  { key: 'shortlisted', label: 'Shortlisted', color: 'bg-purple-500' },
  { key: 'interview_1', label: 'Interview 1', color: 'bg-orange-500' },
  { key: 'interview_2', label: 'Interview 2', color: 'bg-pink-500' },
  { key: 'offer', label: 'Offer', color: 'bg-cyan-500' },
  { key: 'hired', label: 'Hired', color: 'bg-green-500' },
  { key: 'rejected', label: 'Rejected', color: 'bg-red-500' },
];

export default function AdminATSJobsPage() {
  const { language } = useLanguage();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  useEffect(() => {
    fetchJobs();
  }, [statusFilter]);

  const fetchJobs = async () => {
    setLoading(true);
    try {
      let url = `${API}/admin/ats/jobs`;
      if (statusFilter && statusFilter !== 'all') url += `?status=${statusFilter}`;
      
      const response = await fetch(url, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setJobs(data.jobs || []);
      }
    } catch (error) {
      console.error('Error fetching jobs:', error);
      toast.error('Failed to load jobs');
    } finally {
      setLoading(false);
    }
  };

  const filteredJobs = jobs.filter(job =>
    job.title?.toLowerCase().includes(search.toLowerCase()) ||
    job.location?.toLowerCase().includes(search.toLowerCase())
  );

  const getStatusBadge = (status) => {
    const colors = {
      active: 'bg-green-100 text-green-800',
      draft: 'bg-gray-100 text-gray-800',
      closed: 'bg-red-100 text-red-800',
      paused: 'bg-yellow-100 text-yellow-800'
    };
    return <Badge className={colors[status] || 'bg-gray-100'}>{status}</Badge>;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  return (
    <div className="p-6" data-testid="admin-ats-jobs-page">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-[#111827] flex items-center gap-2">
            <Briefcase className="h-6 w-6 text-[#33AADD]" />
            ATS Jobs
          </h1>
          <p className="text-[#6B7280]">Manage job postings and track applicants</p>
        </div>
        <Link to="/admin/jobs">
          <Button variant="outline">
            <Briefcase className="h-4 w-4 mr-2" /> Manage Jobs
          </Button>
        </Link>
      </div>

      {/* Search & Filters */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#6B7280]" />
              <Input
                placeholder="Search jobs..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="closed">Closed</SelectItem>
                <SelectItem value="paused">Paused</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Jobs List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#33AADD]"></div>
        </div>
      ) : filteredJobs.length === 0 ? (
        <Card>
          <CardContent className="text-center py-12 text-[#6B7280]">
            <Briefcase className="h-12 w-12 mx-auto mb-4 text-[#E5E7EB]" />
            <p>No jobs found</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredJobs.map((job) => (
            <Card key={job.job_id} className="hover:border-[#33AADD]/50 transition-colors" data-testid={`job-card-${job.job_id}`}>
              <CardContent className="p-6">
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                  {/* Job Info */}
                  <div className="flex-1">
                    <div className="flex items-start gap-4">
                      <div className="h-12 w-12 rounded-lg bg-[#33AADD]/10 flex items-center justify-center">
                        <Briefcase className="h-6 w-6 text-[#33AADD]" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-[#111827]">{job.title}</h3>
                        <div className="flex flex-wrap items-center gap-3 mt-1 text-sm text-[#6B7280]">
                          {job.location && (
                            <span className="flex items-center gap-1">
                              <MapPin className="h-3.5 w-3.5" /> {job.location}
                            </span>
                          )}
                          <span className="flex items-center gap-1">
                            <Calendar className="h-3.5 w-3.5" /> Posted {formatDate(job.created_at)}
                          </span>
                          {getStatusBadge(job.status)}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Applications Summary */}
                  <div className="flex items-center gap-6">
                    <div className="text-center">
                      <div className="flex items-center gap-2 text-2xl font-bold text-[#111827]">
                        <Users className="h-5 w-5 text-[#6B7280]" />
                        {job.application_count || 0}
                      </div>
                      <p className="text-xs text-[#6B7280]">Total Applicants</p>
                    </div>
                    
                    <Link to={`/admin/applications?job_id=${job.job_id}`}>
                      <Button variant="outline" size="sm">
                        <Eye className="h-4 w-4 mr-1" /> View Pipeline
                        <ChevronRight className="h-4 w-4 ml-1" />
                      </Button>
                    </Link>
                  </div>
                </div>

                {/* Stage Breakdown */}
                {job.application_count > 0 && job.stage_breakdown && Object.keys(job.stage_breakdown).length > 0 && (
                  <div className="mt-4 pt-4 border-t">
                    <p className="text-xs text-[#6B7280] mb-2">Pipeline Breakdown</p>
                    <div className="flex flex-wrap gap-2">
                      {PIPELINE_STAGES.map((stage) => {
                        const count = job.stage_breakdown[stage.key] || 0;
                        if (count === 0) return null;
                        return (
                          <div key={stage.key} className="flex items-center gap-1.5 px-2 py-1 bg-[#F9FAFB] rounded text-sm">
                            <span className={`h-2 w-2 rounded-full ${stage.color}`}></span>
                            <span className="text-[#6B7280]">{stage.label}:</span>
                            <span className="font-medium text-[#111827]">{count}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
